<template>
  <LoadingCircle v-if="isLoading" />
  <div v-else>
    <v-row no-gutters>
      <v-col>
        <StandardCardContainer>
          <template #title>
            <div class="d-flex justify-space-between align-center">
              <span>{{ questionnaireLabel }}</span>
              <PPopUpMenu
                :menu-items="menuItems"
                :menu-icon="'icon-horizontal-more-outline'"
                @menu-item-clicked="click($event)"
              />
            </div>
          </template>

          <div class="info-table text-left">
            <v-row no-gutters>
              <v-col class="divider">
                <p class="header">Created at</p>
                <p>
                  {{ questionnaireCreatedAt }}
                </p>
              </v-col>
              <v-spacer />
              <v-col class="d-flex justify-end align-center">
                <PActionButton
                  :append-icon="true"
                  :icon-size="'xs'"
                  appkit-icon="icon-right-chevron-fill"
                  @click="sendQuestionnaire"
                  >Send</PActionButton
                >
              </v-col>
            </v-row>
          </div>
        </StandardCardContainer>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <RecipientsCard
          v-if="hasRecipients"
          :data="recipientsDataGroupedByTier"
          @unit-selected="routeToUnitProfile"
          @view-answers="routeToAnswers"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <QuestionnaireSectionsCard
          :selected-questionnaire-label="
            questionnairesStore.selectedQuestionnaire.templateId
          "
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import StandardCardContainer from "@/components/containers/StandardCardContainer.vue";
import { RouterPath } from "@/models/enum/routerPath";
import { computed, ref, type Ref } from "vue";
import { useQuestionnairesStore } from "@/stores/questionnaires";
import { useStatsStore } from "@/stores/statsStore";
import { onBeforeMount } from "vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import RecipientsCard from "./RecipientsCard.vue";
import { getAnswerStatus } from "@/services/questionnaireService";
import type { AnswerSetStatusDto } from "@/models/dto/answerSetStatusDto";
import { useReportViewStore } from "@/stores/reportViewStore";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import PPopUpMenu from "@/components/custom/PPopUpMenu.vue";
import type { SideMenuItem } from "@/models/sideMenuItem";
import router from "@/router";
import { useAuthenticationStore } from "@/stores/authentication";
import QuestionnaireSectionsCard from "./QuestionnaireSectionsCard.vue";
import { useSuppliersStore } from "@/stores/suppliersStore";
import { useUnitProfileStore } from "@/stores/unitProfileStore";
import { type GroupedReceivers } from "@/models/groupedReceivers";

const questionnairesStore = useQuestionnairesStore();
const statsStore = useStatsStore();
const snackBarStore = useSnackBarStore();
const reportsStore = useReportViewStore();
const authenticationStore = useAuthenticationStore();
const suppliersStore = useSuppliersStore();
const unitProfileStore = useUnitProfileStore();

const isLoading = ref(true);
const recipientsData: Ref<AnswerSetStatusDto[]> = ref([]);

const recipientsDataGroupedByTier = computed(() => {
  const groupedRecipients: GroupedReceivers = {};

  recipientsData.value.forEach((r) => {
    const br = r.isBusinessPartner ? "Business partners" : `Tier ${r.tier}`;

    if (!groupedRecipients[br]) {
      groupedRecipients[br] = [];
    }

    const mappedReceiver = {
      contextId: r.contextId,
      companyName: r.receiverCompanyName,
      email: r.receiverEmail,
      activeUserEmail: r.activeUserEmail,
      senderCompanyNames: r.senderCompanyNames.join(", "),
      lastEdited: r.lastEdited,
      answerStatus: r.answerStatus,
      forwardingStatus: r.forwardingStatus,
      dueDate: r.dueDate,
      sentDate: r.sentDate,
      sentReminders: r.sentReminders.length.toString(),
      sentRemindersDates: r.sentReminders,
      countryCode: r.countryCode,
    };

    groupedRecipients[br].push(mappedReceiver);
  });
  return groupedRecipients;
});

const hasRecipients = computed(() => recipientsData.value.length > 0);
const questionnaireLabel = computed(
  () => questionnairesStore.selectedQuestionnaire.label
);

const questionnaireCreatedAt = computed(() =>
  questionnairesStore.getCreatedAtDateString()
);

const exportDataUrl = computed(
  () =>
    `/api/export/supply-chain?questionnaireId=${questionnairesStore.selectedQuestionnaire.id}`
);

const exportQuestionnaireAnswersUrl = computed(
  () =>
    `/api/export/questionnaire-answers?questionnaireId=${questionnairesStore.selectedQuestionnaire.id}`
);

const menuItems = ref([
  {
    id: "Rename",
    title: "Rename",
    path: RouterPath.EditQuestionnaire,
    icon: "icon-edit-outline",
    visible: true,
  },
  {
    id: "DownloadData",
    title: "Download summary data",
    icon: "icon-download-cloud-outline",
    path: exportDataUrl,
    visible:
      authenticationStore.isSuperUser || authenticationStore.isChannel2Customer,
  },
  {
    id: "DownloadAllAnswers",
    title: "Download all answers",
    icon: "icon-document-text-outline",
    path: exportQuestionnaireAnswersUrl,
    visible:
      authenticationStore.isSuperUser || authenticationStore.isChannel2Customer,
  },
]);
onBeforeMount(async () => {
  try {
    await statsStore.setStatsSummary(
      questionnairesStore.selectedQuestionnaire.id
    );
    recipientsData.value = await getAnswerStatus(
      questionnairesStore.selectedQuestionnaire.id
    );
    reportsStore.selectedQuestionnaireLabel = questionnaireLabel.value;
  } catch (error) {
    snackBarStore.showError();
  } finally {
    isLoading.value = false;
  }
});

function click(event: SideMenuItem) {
  if (event.id == "DownloadData") {
    downloadExportInNewTab(event.path);
  }
  if (event.id == "DownloadAllAnswers") {
    downloadExportInNewTab(event.path);
  }
  if (event.id == "Rename") {
    router.push(event.path);
  } else {
    router.push(RouterPath.MyQuestionnaireStatus);
  }
}

function sendQuestionnaire() {
  if (!hasRecipients.value && !suppliersStore.hasSuppliers) {
    router.push(RouterPath.UploadSuppliers);
  } else {
    router.push(RouterPath.SendQuestionnaire);
  }
}

async function routeToUnitProfile(contextId: string) {
  try {
    isLoading.value = true;
    await unitProfileStore.getUnitProfileWithContextId(
      questionnairesStore.selectedQuestionnaire.id,
      contextId
    );
    await router.push(RouterPath.UnitProfile);
  } catch (error: any) {
    snackBarStore.showError(error.message);
  } finally {
    isLoading.value = false;
  }
}

async function routeToAnswers(contextId: string, companyName: string) {
  try {
    isLoading.value = true;
    reportsStore.selectedContextId = contextId;
    reportsStore.selectedQuestionnareId =
      questionnairesStore.selectedQuestionnaire.id;
    reportsStore.selectedQuestionnaireLabel = questionnaireLabel.value;
    reportsStore.selectedReceiverCompanyName = companyName;
    await router.push(RouterPath.QuestionnaireAnswers);
  } catch (error: any) {
    snackBarStore.showError(error.message);
  } finally {
    isLoading.value = false;
  }
}

function downloadExportInNewTab(url: string) {
  window.open(url);
}
</script>

<style scoped lang="scss">
.info-table {
  margin-top: 12px;
  .header {
    color: var(--grey);
  }
  .divider {
    padding-left: 12px;
    border-left: 1px solid var(--light-grey-1);
  }
}
</style>
